import * as React from 'react';
import styled from 'styled-components';
import { Viewer } from 'constants/user';
import Modal, { ModalWidths } from 'components/Modal';
import { RawButton, CTAButton, CancelButton, SolidButton } from 'styles/elements';
import { IoExitOutline } from 'react-icons/io5';
import { BsCheck2Circle } from 'react-icons/bs';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { useUpdateTradingViewUsernameMutation } from 'types/generated/client';
import Sentry from 'utils/shared/sentry';
import MiniInput from 'components/MiniInput';
import Card from 'components/Card';
import {
  TradingViewAccessStatus,
  TV_RESOURCE_CENTER_LINK,
  getTradingViewProfileUrl,
  getTradingViewProfileUrlWithAffiliate,
  getTradingviewUsernameFromProfileUrl,
} from 'constants/tradingview';
import { submitTradingviewUsername } from 'services/client/pipedream';

export const UsernameButton = styled(RawButton)`
  display: flex;
  border-left: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 0.15rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  transition: background-color 200ms ease;
  color: ${(props) => props.theme.color.textPrimary};
  border: 2px solid ${(props) => props.theme.color.cta};
  background-color: ${(props) => props.theme.color.cta};
  font-weight: 600;

  &:hover {
    background-color: ${(props) => props.theme.color.textPrimary};
    border-color: ${(props) => props.theme.color.textPrimary};
    color: ${(props) => props.theme.color.cta};
  }
`;

export const SubmitButton = styled(CTAButton)``;

export const ChangeUsernameButton = styled(CancelButton)`
  background: transparent;
  border-color: white;
`;

interface Props {
  viewer: Viewer;
  existingTradingviewUsername?: string;
  tradingviewAccessStatus?: string;
  isLearnMoreHidden?: boolean;
}

const AddTradingviewUsername: React.FC<React.PropsWithChildren<Props>> = ({
  viewer,
  existingTradingviewUsername,
  tradingviewAccessStatus,
  isLearnMoreHidden,
}) => {
  const [
    updateTradingViewUsername,
    { error: tradingViewError, loading: tradingViewLoading, called },
  ] = useUpdateTradingViewUsernameMutation();
  const [tradingviewProfileUrl, setTradingviewProfileUrl] = React.useState('');
  const [isTradingViewModalOpen, setIsTradingViewModalOpen] = React.useState(false);
  const [isTradingViewErrorModalOpen, setIsTradingViewErrorModalOpen] = React.useState(false);
  const shouldShowTradingviewStatus =
    !!existingTradingviewUsername || (called && !tradingViewError);

  let tradingviewUsername = '';
  try {
    tradingviewUsername = getTradingviewUsernameFromProfileUrl(tradingviewProfileUrl);
  } catch (e) {}

  return (
    <>
      <div>
        <div className="styled-font mb-2 text-xl font-semibold">
          Algo Trading System on TradingView
        </div>
        <Card>
          {!!tradingViewError && (
            <div className="mb-1 text-sm text-red-400">
              There was an issue adding your TradingView profile. Try a different profile URL or
              return later.
            </div>
          )}
          <div className="flex flex-col items-start justify-start md:flex-row md:items-center md:justify-between">
            <div>
              <div className="mb-1 text-lg font-semibold">Add Your TradingView Profile URL</div>
              <div className="pr-6 text-xs text-gray-400 sm:pr-12">
                Included free with your subscription is our award-nominated technical analysis
                system! It only takes a few minutes to access the tools directly on TradingView with
                a free TradingView account (no additional subscription required). Follow our quick
                step-by-step instructions to locate and enter your TradingView Profile URL.
              </div>
              {!isLearnMoreHidden && (
                <div>
                  <a
                    href={TV_RESOURCE_CENTER_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 flex items-center text-xs text-gray-300 underline"
                  >
                    <span className="mr-2">Learn How To Access</span> <IoExitOutline />
                  </a>
                </div>
              )}
            </div>
            {shouldShowTradingviewStatus ? (
              <div className="flex flex-shrink-0 items-center rounded border border-gray-400 p-4 text-sm">
                {tradingviewAccessStatus === TradingViewAccessStatus.Active ? (
                  <>
                    <BsCheck2Circle className="mr-2 text-lg text-green-500" /> You Have Access
                  </>
                ) : (
                  <>
                    {tradingviewAccessStatus === TradingViewAccessStatus.Pending ? (
                      <>
                        <BsCheck2Circle className="mr-2 text-lg text-yellow-300" /> Access Pending
                      </>
                    ) : (
                      <>
                        <AiOutlineCloseCircle className="mr-2 text-lg text-red-500" /> Access
                        Removed
                      </>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="mt-4 flex-shrink-0 md:mt-0" style={{ minWidth: '280px' }}>
                <MiniInput
                  placeholder="TradingView profile URL..."
                  value={tradingviewProfileUrl}
                  onChange={(e) => {
                    if (!tradingViewLoading) {
                      setTradingviewProfileUrl(e.target.value);
                    }
                  }}
                  reset={() => {
                    setTradingviewProfileUrl('');
                  }}
                  button={
                    <UsernameButton
                      className="flex-shrink-0 items-center justify-center px-4 py-2 text-xs"
                      type="button"
                      disabled={tradingViewLoading}
                      onClick={() => {
                        if (
                          !tradingviewProfileUrl ||
                          !tradingviewProfileUrl.trim() ||
                          tradingViewLoading ||
                          !viewer?.viewer?.uid
                        ) {
                          return;
                        }

                        if (!tradingviewProfileUrl.includes('tradingview.com/u/')) {
                          setIsTradingViewErrorModalOpen(true);
                          return;
                        }

                        if (
                          !tradingviewUsername ||
                          !tradingviewUsername.trim() ||
                          tradingViewLoading ||
                          !viewer?.viewer?.uid
                        ) {
                          setIsTradingViewErrorModalOpen(true);
                          return;
                        }

                        // if (tradingviewUsername.includes('@')) {
                        //   setIsTradingViewErrorModalOpen(true);
                        //   return;
                        // }

                        setIsTradingViewModalOpen(true);
                      }}
                    >
                      Submit
                    </UsernameButton>
                  }
                />
              </div>
            )}
          </div>
        </Card>
      </div>
      <Modal
        titleText="Set TradingView username"
        onExit={() => setIsTradingViewModalOpen(false)}
        mounted={isTradingViewModalOpen}
        focusDialog
        verticallyCenter
        width={ModalWidths.Medium}
      >
        <div className="p-8">
          <div className="mb-2 text-lg font-semibold">Add Your TradingView Profile URL</div>
          <div className="text-center">
            <div className="mt-4 text-sm">
              Please confirm that your TradingView username is correct. You can click the link below
              to ensure it takes you to the TradingView profile that you wish to use to access the
              InsiderFinance technical analysis system.
            </div>
            <div className="my-3 text-xl text-green-400">{tradingviewUsername}</div>
            <div>
              <a
                href={getTradingViewProfileUrlWithAffiliate(
                  getTradingViewProfileUrl(tradingviewUsername),
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {getTradingViewProfileUrl(tradingviewUsername)}
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ChangeUsernameButton onClick={() => setIsTradingViewModalOpen(false)}>
                Change Profile URL
              </ChangeUsernameButton>
              <SubmitButton
                type="button"
                disabled={tradingViewLoading}
                onClick={async () => {
                  if (
                    !tradingviewUsername ||
                    !tradingviewUsername.trim() ||
                    tradingViewLoading ||
                    !viewer?.viewer?.uid
                  ) {
                    return;
                  }

                  setIsTradingViewModalOpen(false);

                  try {
                    await updateTradingViewUsername({
                      variables: {
                        id: viewer.viewer.uid,
                        tradingviewUsername: tradingviewUsername.toLowerCase(),
                        tradingviewUsernameCaseSensitive: tradingviewUsername,
                      },
                    });

                    await submitTradingviewUsername({
                      id: viewer.viewer.uid,
                      email: viewer.viewer.email,
                      tradingviewUsername: tradingviewUsername.toLowerCase(),
                      tradingviewUsernameCaseSensitive: tradingviewUsername,
                    });
                  } catch (error) {
                    // NOTE: Error is in the mutation response
                    Sentry.captureException(error);
                  }
                }}
              >
                Confirm
              </SubmitButton>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        width={ModalWidths.Medium}
        titleText="tradingview-error"
        onExit={() => setIsTradingViewErrorModalOpen(false)}
        mounted={isTradingViewErrorModalOpen}
        verticallyCenter
        focusDialog
        useCloseButton={false}
        scrollDisabled
      >
        <div className="relative p-8">
          <button
            aria-label="Close dialog"
            onClick={() => setIsTradingViewErrorModalOpen(false)}
            className="safearea-pad-top absolute right-4 top-2 text-4xl leading-none"
          >
            &times;
          </button>
          <div className="text-2xl font-bold">Profile URL Invalid</div>
          <div className="mb-3 mt-5 text-sm text-gray-300">
            Your TradingView <span className="text-green-400">profile URL</span> is invalid. If
            you're logged in to TradingView, you can{' '}
            <a
              href="https://www.tradingview.com/u/?offer_id=10&aff_id=28670"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              follow this link to be taken to your profile
            </a>
            . Copy and paste that URL, and we can determine the username to give you access.
          </div>
          {!isLearnMoreHidden && (
            <div>
              <a
                href={TV_RESOURCE_CENTER_LINK}
                className="font-semibold underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                See our step-by-step guide to find your profile URL
              </a>
            </div>
          )}
          <div className="mt-9">
            <SolidButton onClick={() => setIsTradingViewErrorModalOpen(false)}>
              Close and Enter Valid Profile URL
            </SolidButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AddTradingviewUsername;
